import type { JSX } from 'react';
import type { Locale } from '../i18n';
import { widgetText } from './copy';

/**
 * La salida del comprador hacia la tienda.
 *
 * <p>Vive suelto porque lo necesitan DOS pantallas por motivos distintos —el selector, donde el
 * comprador todavia no ha pagado y puede querer corregir algo, y las terminales, donde ya hay
 * desenlace— y la regla de cual URL es valida no puede tener dos versiones.
 *
 * <p><strong>Se usa `merchant_return_url` y no `return_url`, que no es lo mismo.</strong>
 * `return_url` es donde vuelve LA PASARELA, y en un checkout alojado la compone la plataforma
 * apuntando a este mismo widget --con el `client_secret` en la query, que es lo unico que sobrevive
 * a la redireccion--: mandar alli al comprador seria dejarlo donde ya esta. `merchant_return_url`
 * es donde vuelve EL COMPRADOR, de regreso a la tienda, y es el unico de los dos que el comercio
 * controla.
 *
 * <p><strong>Y no se intenta «volver por donde vino».</strong> Ni `document.referrer` ni
 * `history.back()` sirven aqui: al checkout alojado se llega por redireccion y la mayoria de las
 * tiendas mandan un `Referrer-Policy` que lo vacia; y despues de una ida y vuelta a la pasarela el
 * historial tiene entradas que no son de la tienda. Adivinar la salida es peor que no ofrecerla.
 *
 * <p><strong>Una URL DE ESTE MISMO ORIGEN tampoco es una salida.</strong> Hasta el 2026-09-16 la
 * guia del checkout alojado le pedia al comercio que su `return_url` apuntara AQUI, a
 * `<checkout>/session?pk=...`. Desde el arreglo de esa fecha la `return_url` de la pasarela la
 * compone la plataforma y la que declara el comercio pasa tal cual a `merchant_return_url`, asi que
 * los integradores que siguieron la guia vieja tienen intents cuyo "Volver a la tienda" apunta a
 * esta misma pagina. Ese boton no lleva a ninguna parte --recarga el checkout, y desde una pantalla
 * terminal vuelve a pintar la misma pantalla-- y encima miente sobre a donde lleva. Sin salida
 * creible es mejor no ofrecer ninguna: la pantalla ya cuenta el desenlace, que es lo que el
 * comprador vino a saber.
 */
export function safeMerchantUrl(candidate: string | undefined): string | null {
  if (candidate === undefined || candidate === '') {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return null;
  }
  // Mismo origen que el checkout: no es la tienda, somos nosotros. Ver la nota de arriba.
  if (parsed.origin === window.location.origin) {
    return null;
  }
  return candidate;
}

/**
 * Lleva al comprador a la tienda sin que pulse nada.
 *
 * <p>`window.top` y no `window.location`, por el mismo motivo por el que el enlace usa
 * `target="_top"`: si el checkout viviera en un iframe, navegar el documento propio dejaria la
 * tienda DENTRO del recuadro del checkout. Quien llama decide cuando procede --hoy, solo en modo
 * pagina--. La URL se vuelve a validar aqui: esta funcion navega sola, sin un click de por medio.
 */
export function navegarALaTienda(href: string): void {
  const destino = safeMerchantUrl(href);
  if (destino === null) {
    return;
  }
  (window.top ?? window).location.assign(destino);
}

export interface MerchantReturnLinkProps {
  /** `merchant_return_url` del intent; si no es una URL http(s) usable, no se pinta nada. */
  merchantReturnUrl: string | undefined;
  locale: Locale;
  /** Clases del boton: primario en las pantallas terminales, secundario en el selector. */
  className: string;
  /**
   * Salida EMPOTRADA, si la pantalla tiene una.
   *
   * <p>Cuando el widget vive en un iframe dentro de la tienda, el comercio YA ES la pagina de
   * alrededor: llevarse la ventana entera a `merchant_return_url` recargaria su propio sitio para
   * dejar al comprador donde practicamente ya estaba. Lo que hace falta ahi es que el checkout se
   * quite de en medio, y quien sabe como hacerlo --cerrar el modal, restaurar el carrito-- es el
   * comercio. Por eso, con este callback presente y empotrado, se le AVISA en vez de navegar.
   *
   * <p>Sin callback --las pantallas terminales-- se navega igual, porque alli el aviso ya se emitio
   * con el desenlace y volver a la tienda es exactamente lo que toca.
   */
  onEmbeddedReturn?: (() => void) | undefined;
  /** Si el widget esta empotrado. En modo pagina el enlace navega y punto. */
  embedded?: boolean | undefined;
}

/**
 * Enlace de vuelta a la tienda, o {@code null} cuando el comercio no declaro ninguna.
 *
 * <p><strong>`target="_top"` no es opcional.</strong> Empotrado, el widget vive en un iframe dentro
 * de la pagina del comercio: sin eso, «volver a la tienda» cargaria la tienda DENTRO del iframe del
 * checkout, y el comprador acabaria navegando su propia tienda en un recuadro. Con `_top` la
 * navegacion es de la ventana entera, que es lo que el boton promete; el click aporta la activacion
 * de usuario que el navegador exige para dejar salir a un iframe de otro origen.
 */
export function MerchantReturnLink({
  merchantReturnUrl,
  locale,
  className,
  onEmbeddedReturn,
  embedded,
}: MerchantReturnLinkProps): JSX.Element | null {
  const href = safeMerchantUrl(merchantReturnUrl);
  const label = widgetText(locale, 'status.back_to_merchant');

  if (embedded === true && onEmbeddedReturn !== undefined) {
    // Empotrado se AVISA al comercio en vez de navegar (ver onEmbeddedReturn). El aviso lleva
    // ademas a la pantalla de cancelado, que vuelve a ofrecer la vuelta como enlace de verdad: si
    // el comercio no escucha el evento, el comprador sigue teniendo salida.
    return (
      <button
        type="button"
        data-testid="merchant-return"
        className={className}
        onClick={onEmbeddedReturn}
      >
        {label}
      </button>
    );
  }

  if (href === null) {
    return null;
  }
  return (
    <a
      data-testid="merchant-return"
      href={href}
      target="_top"
      rel="noopener"
      className={`${className} no-underline`}
    >
      {label}
    </a>
  );
}
